import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, clearToken, getToken, setDataSource } from "../lib/api";
import { localApi, setCnToken, clearCnToken } from "../lib/localApi";
import { toast } from "sonner";
import i18n from "../i18n";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// Hydrate `casaneo_data_source` from the backend so a fresh browser (post
// /reset or first-ever login) doesn't fall back to `legacy` and hit a
// tokenless external API. Best-effort — silently skips on failure so we
// don't block bootstrap.
async function _hydrateDataSource() {
  try {
    const { data } = await localApi.get("/migration/data-source");
    if (data?.source) setDataSource(data.source);
  } catch {
    /* noop */
  }
}

// Ask the backend who the current user *really* is (auth_type + is_admin
// resolved against `db.owners`). This is authoritative — the frontend
// user object built from disparate login sources can be missing
// `auth_type` and misclassify admins as owners.
async function _hydrateWhoami(setUser, attempt = 0) {
  try {
    const { data } = await localApi.get("/local/whoami");
    if (data?.language && data.language !== i18n.resolvedLanguage) i18n.changeLanguage(data.language);
    if (data && typeof data.is_admin === "boolean") {
      // Compte propriétaire : aucun jeton legacy ne doit subsister (il est
      // partagé avec l'agence côté ancien système).
      if (data.is_admin === false) clearToken();
      setUser((u) => ({
        ...(u || {}),
        // Preserve caller fields, but always mirror the authoritative flags.
        email: u?.email || data.email,
        role: u?.role || data.role,
        auth_type: data.auth_type || u?.auth_type || null,
        tenant_id: u?.tenant_id || data.tenant_id,
        is_admin_resolved: data.is_admin,
        account_kind: data.account_kind || null,
        owner_id: u?.owner_id || data.owner_id || null,
        name: u?.name || data.name,
        owner_permissions: data.permissions || null,
      }));
    }
  } catch (e) {
    const st = e?.response?.status;
    if (!st && attempt < 1) {
      // Réseau indisponible : on réessaie une fois avant de conclure.
      setTimeout(() => _hydrateWhoami(setUser, attempt + 1), 2000);
      return;
    }
    if (st === 401) {
      // Session Casanéo morte (jeton expiré, cookie perdu sur tablette…) : on ne
      // rabat JAMAIS un gestionnaire sur l'espace propriétaire, on redemande la connexion.
      clearToken();
      clearCnToken();
      try { localStorage.removeItem("casaneo_user"); } catch { /* noop */ }
      setUser(null);
      if (!window.location.pathname.startsWith("/login")) {
        toast.error("Votre session a expiré — reconnectez-vous.");
        window.location.replace("/login");
      }
      return;
    }
    // FAIL-CLOSED : sans confirmation du backend, la session n'est pas admin.
    setUser((u) => (u ? { ...u, is_admin_resolved: u.role === "admin" || u.auth_type === "api_key" ? u.is_admin_resolved ?? null : false } : u));
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const bootstrap = useCallback(async () => {
    // CRITICAL: si retour du callback OAuth, laisser AuthCallback gérer l'échange session_id.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Two sessions may exist (legacy + Casanéo cookies). Restore
    // whichever answers `me` — this lets Casanéo-only accounts survive
    // page reloads without a legacy token.
    let legacyRejected = false;
    // Casanéo d'abord : c'est la seule source fiable pour le rôle (le legacy
    // renvoie role=null/"owner" même pour un gestionnaire → page propriétaire).
    try {
      const { data } = await localApi.get("/auth/me");
      if (data?.email) {
        setUser(data);
        try { localStorage.setItem("casaneo_user", JSON.stringify({ email: data?.email, name: data?.name })); } catch { /* noop */ }
        _hydrateDataSource();
        _hydrateWhoami(setUser);
        setLoading(false);
        return;
      }
    } catch { /* pas de session Casanéo : on tente le legacy ci-dessous */ }
    if (getToken()) {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        try { localStorage.setItem("casaneo_user", JSON.stringify({ email: data?.email, name: data?.name })); } catch { /* noop */ }
        _hydrateDataSource();
        _hydrateWhoami(setUser);
        setLoading(false);
        return;
      } catch (e) {
        // A slow/unreachable legacy host must NOT cost the user his token:
        // only an explicit 401 means the session is really dead.
        legacyRejected = e?.response?.status === 401;
      }
    }
    try {
      const { data } = await localApi.get("/auth/me");
      setUser(data);
      try { localStorage.setItem("casaneo_user", JSON.stringify({ email: data?.email, name: data?.name })); } catch { /* noop */ }
      _hydrateDataSource();
      _hydrateWhoami(setUser);
    } catch {
      if (legacyRejected) clearToken();
      clearCnToken();
      setUser(null);
      try { localStorage.removeItem("casaneo_user"); } catch { /* noop */ }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = async (email, password) => {
    // The SPA authenticates against TWO backends that don't share user
    // stores: the legacy `rental-hub-manager` host (session_token in
    // localStorage) and the Casanéo FastAPI (httpOnly cookies). Some
    // accounts only exist on one side (e.g. Casanéo has an admin user
    // for whom the legacy host returns 401), so we must NOT gate one
    // login behind the other.
    const [legacy, casaneo] = await Promise.allSettled([
      api.post("/auth/login", { email, password }),
      localApi.post("/auth/login", { email, password }),
    ]);

    // Prefer the legacy user object when available (it carries the SaaS
    // profile), fall back to the Casanéo one otherwise.
    let userData = null;
    // Jeton legacy d'une session précédente (autre compte sur ce navigateur) :
    // on le purge dès que le legacy refuse ces identifiants.
    if (legacy.status === "rejected") clearToken();
    if (legacy.status === "fulfilled") {
      const data = legacy.value.data;
      if (data?.session_token) setToken(data.session_token);
      userData = data?.user || null;
    }
    const casaneoUser = casaneo.status === "fulfilled" ? (casaneo.value.data?.user || null) : null;
    // Casanéo est la seule source de vérité : si Casanéo refuse (mot de passe,
    // trop de tentatives, panne…), la connexion échoue — même si l'ancien
    // serveur accepte encore l'ancien mot de passe. Sinon l'utilisateur voyait
    // le tableau de bord une seconde puis retombait sur la page d'accueil
    // publique (whoami 401), sans aucun message (bug tablette, sept. 2026).
    if (casaneo.status === "rejected") {
      clearToken();
      throw casaneo.reason;
    }
    const legacyIsOwnerAccount = legacy.status === "fulfilled"
      && (userData?.role || "").toLowerCase() === "owner"
      && userData?.auth_type !== "api_key";
    if (["owner", "cleaner"].includes((casaneoUser?.role || "").toLowerCase()) || legacyIsOwnerAccount) {
      clearToken();
      userData = casaneoUser || userData;
    }
    // Rôle équipe confirmé par Casanéo : l'objet Casanéo fait foi (le legacy
    // renvoie role=null pour un gestionnaire, ce qui verrouillait la vue propriétaire).
    if (["admin", "manager", "staff"].includes((casaneoUser?.role || "").toLowerCase())) {
      userData = { ...(userData || {}), ...casaneoUser };
    }
    if (casaneo.status === "fulfilled") {
      const data = casaneo.value.data;
      // Belt & suspenders: the localApi response interceptor already
      // captures access_token from /auth/login, but keep this as a hard
      // guarantee in case the interceptor was somehow bypassed.
      if (data?.access_token) setCnToken(data.access_token);
      // Legacy fallback token — Casanéo mints one for admins so all the
      // still-legacy endpoints (calendars, dashboard, cleaning-schedule,
      // preferences, inbox, analytics/revenue…) keep working. Without
      // this, a fresh `/reset` browser floods the console with 401s.
      if (!getToken() && data?.legacy_session_token) {
        setToken(data.legacy_session_token);
      }
      userData = userData || data?.user || data || null;
    }

    // Fresh login = fresh view mode. Wipe any stale `owner` value from a
    // previous session so admins never land back inside owner-preview after
    // re-authenticating. (Real owner accounts are re-forced to owner by
    // `ViewRoleContext` on the next render.)
    try { localStorage.removeItem("casaneo_view_mode"); } catch { /* noop */ }

    // Also hydrate the data source so the frontend queries the correct
    // backend (Mongo vs legacy) right away — no need to wait for the user
    // to visit Settings.
    await _hydrateDataSource();

    setUser(userData);
    // Backend-authoritative RBAC — refresh in the background.
    _hydrateWhoami(setUser);
    return { user: userData };
  };

  const completeGoogle = (sessionToken, u) => {
    if (sessionToken) setToken(sessionToken);
    setUser(u || null);
  };

  // Session Casanéo déjà ouverte (CasaneoAuthContext) mais AuthContext vide :
  // on l'adopte au lieu d'afficher la page d'accueil publique.
  const adoptCasaneoUser = async (u) => {
    if (!u) return;
    await _hydrateDataSource();
    setUser((cur) => cur || u);
    _hydrateWhoami(setUser);
  };

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      /* noop */
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* noop */
    }
    try { await localApi.post("/auth/logout"); } catch { /* noop */ }
    clearToken();
    clearCnToken();
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, completeGoogle, refresh, setUser, adoptCasaneoUser }}>
      {children}
    </AuthContext.Provider>
  );
}
